import React, {useRef, useState} from 'react';

import {STATSIG_GATES} from 'src/contexts/statsig-contex/constants';

import Button, {CompoundButton} from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {DropdownMenu, DropdownMenuItem} from '@pb/kit-component-dropdown-menu';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {Message} from '@pb/kit-component-message';
import {useGateValue} from '@pb/kit-lib-statsig';

import {ReasonField, isValidReason} from './reason-field';

import type {AuditReasonRequest} from 'src/openapi/admin/models/AuditReasonRequest';

export type ActionOptions = AuditReasonRequest;
export type ActionWithReason = (options?: ActionOptions) => Promise<string | undefined>;


type Props = {
    onConfirm: ActionWithReason,
    action: string,
    legacyLabel?: string,
    confirmation?: {
        title: string,
        message: React.ReactNode,
        width: number,
    },
    variant?: React.ComponentProps<typeof Button>['variant'],
    colorVariant?: React.ComponentProps<typeof Button>['colorVariant'],
    busy?: boolean,
    disabled?: boolean,
};

export const ActionWithReasonButton = ({
    onConfirm, action, legacyLabel = action, confirmation,
    variant = 'Type.PRIMARY', colorVariant = 'DEFAULT', busy = false, disabled = false,
}: Props) => {
    const provisioningHistoryEnabled = useGateValue(STATSIG_GATES.ACCOUNT_PROVISIONING_HISTORY);
    const [shown, setShown] = useState(false);
    const [withReason, setWithReason] = useState(false);
    const [reason, setReason] = useState('');
    const [error, setError] = useState<string>();
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);
    const pending = busy || saving;
    const reasonRequired = provisioningHistoryEnabled && withReason;
    const isConfirmDisabled = pending || disabled || (reasonRequired && !isValidReason(reason));


    const close = () => {
        if (busy || savingRef.current) {
            return;
        }
        setShown(false);
        setReason('');
        setError(undefined);
    };

    const save = async (options?: ActionOptions) => {
        if (disabled || busy || savingRef.current) {
            return;
        }
        savingRef.current = true;
        setSaving(true);
        setError(undefined);
        try {
            const saveError = await onConfirm(options);
            if (saveError) {
                setError(saveError);
            } else {
                setShown(false);
                setReason('');
            }
        } catch {
            setError('Unable to complete this action. Please try again.');
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    const open = (includeReason: boolean) => {
        setReason('');
        setError(undefined);
        setWithReason(includeReason);
        setShown(true);
    };

    const withoutReason = () => {
        if (confirmation) {
            open(false);
        } else {
            save();
        }
    };

    return (
        <>
            {!shown && error ? <Message color="error">{error}</Message> : null}
            <Dialog
                shown={shown}
                setShown={close}
                wrapperProps={{style: {width: reasonRequired ? 600 : confirmation?.width}}}
                anchor={({anchorRef: dialogAnchorRef, getAnchorProps: getDialogAnchorProps}) => (
                    provisioningHistoryEnabled ? (
                        <CompoundButton
                            variant={variant}
                            primaryButton={{
                                ...getDialogAnchorProps(),
                                ref: dialogAnchorRef,
                                colorVariant,
                                children: `${action} With Reason`,
                                busy: pending,
                                disabled: disabled || pending,
                                onClick: () => open(true),
                            }}
                            renderSecondaryButton={props => (
                                <DropdownMenu anchor={({getAnchorProps, anchorRef, positionRef}) => (
                                    <Button
                                        {...props}
                                        {...getAnchorProps()}
                                        ref={anchorRef}
                                        colorVariant={colorVariant}
                                        disabled={disabled || pending}
                                        aria-label={`More ${action.toLowerCase()} options`}
                                        startIcon={<Icon type={Icons.ANGLE_DOWN} ref={positionRef}/>}
                                    />
                                )}>
                                    <DropdownMenuItem caption={`${action} Without Reason`} onClick={withoutReason}/>
                                </DropdownMenu>
                            )}
                        />
                    ) : (
                        <Button
                            {...(confirmation ? getDialogAnchorProps() : {})}
                            ref={confirmation ? dialogAnchorRef : undefined}
                            variant={variant}
                            colorVariant={colorVariant}
                            busy={pending}
                            disabled={disabled || pending}
                            onClick={withoutReason}>
                            {legacyLabel}
                        </Button>
                    )
                )}>
                <DialogHeader onClose={close}>{confirmation?.title ?? `${action}`}</DialogHeader>
                <DialogMain style={{padding: 24}}>
                    {error ? <Message color="error">{error}</Message> : null}
                    {confirmation ? <p>{confirmation.message}</p> : null}
                    {reasonRequired ? (
                        <ReasonField reason={reason} onChange={setReason} pending={pending}/>
                    ) : null}
                </DialogMain>
                <DialogFooter style={{justifyContent: 'flex-start'}}>
                    <Group>
                        <Button
                            variant="Type.PRIMARY"
                            busy={pending}
                            disabled={isConfirmDisabled}
                            onClick={() => save(reasonRequired ? {reason: reason.trim()} : undefined)}>
                            {action}
                        </Button>
                        <Button variant="Type.SECONDARY" disabled={pending} onClick={close}>Cancel</Button>
                    </Group>
                </DialogFooter>
            </Dialog>
        </>
    );
};
